/**
 * PublicRegistrationPage.js — Public registration page for league sign-ups.
 * Route: /register/:leagueId (public, no auth required)
 *
 * Three registration flows:
 *   Team mode — captain:  registers a new team (creates Team record)
 *   Team mode — player:   joins an existing team
 *   Individual mode:      registers as an individual player
 */

import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Logo from './Logo';
import { getPublicRegistrationForm, submitRegistration, createCheckoutSession, checkRegistrantEmail } from '../services/api';
import './PublicRegistrationPage.css';

// ---------------------------------------------------------------------------
// Custom field renderer
// ---------------------------------------------------------------------------

function CustomField({ field, value, onChange }) {
  const handleChange = (val) => onChange(field.id, val);

  if (field.fieldType === 'dropdown') {
    return (
      <select
        className="pub-reg-input"
        value={value || ''}
        onChange={e => handleChange(e.target.value)}
        required={field.required}
      >
        <option value="">Select...</option>
        {(field.options || []).map(opt => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    );
  }

  if (field.fieldType === 'checkbox') {
    return (
      <label className="pub-reg-checkbox-label">
        <input
          type="checkbox"
          checked={!!value}
          onChange={e => handleChange(e.target.checked)}
        />
        <span>{field.required ? 'Required' : 'Optional'}</span>
      </label>
    );
  }

  if (field.fieldType === 'waiver') {
    return (
      <div className="pub-reg-waiver">
        <div className="pub-reg-waiver-text">
          {field.label}
        </div>
        <label className="pub-reg-checkbox-label">
          <input
            type="checkbox"
            checked={!!value}
            onChange={e => handleChange(e.target.checked)}
            required={field.required}
          />
          <span>I have read and agree to the above</span>
        </label>
      </div>
    );
  }

  if (field.fieldType === 'file_upload') {
    return (
      <p className="pub-reg-field-note">
        File upload is not available online. Please send your file directly to the league organizer.
      </p>
    );
  }

  // Default: text input
  return (
    <input
      type="text"
      className="pub-reg-input"
      value={value || ''}
      onChange={e => handleChange(e.target.value)}
      required={field.required}
    />
  );
}

// ---------------------------------------------------------------------------
// Role selector card
// ---------------------------------------------------------------------------

function RoleCard({ selected, onClick, title, description, icon }) {
  return (
    <button
      type="button"
      className={`pub-reg-role-card ${selected ? 'pub-reg-role-card-selected' : ''}`}
      onClick={onClick}
    >
      <span className="pub-reg-role-icon">{icon}</span>
      <span className="pub-reg-role-title">{title}</span>
      <span className="pub-reg-role-desc">{description}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Not open state
// ---------------------------------------------------------------------------

function ClosedState({ league, closedStatus }) {
  const title = closedStatus === 'draft' ? 'Registration Not Yet Open' : 'Registration is Closed';
  const msg = closedStatus === 'draft'
    ? 'Registration for this league hasn\'t opened yet. Check back later or contact the league organizer.'
    : 'Registration for this league is no longer accepting new sign-ups.';

  return (
    <div className="pub-reg-state-card">
      <div className="pub-reg-state-icon">
        {closedStatus === 'draft' ? '⏳' : '🔒'}
      </div>
      <h2 className="pub-reg-state-title">{title}</h2>
      <p className="pub-reg-state-msg">{msg}</p>
      {league?.slug && (
        <a
          href={`/public/${league.slug}`}
          className="pub-reg-state-link"
        >
          View league page →
        </a>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Not found state
// ---------------------------------------------------------------------------

function NotFoundState() {
  return (
    <div className="pub-reg-state-card">
      <div className="pub-reg-state-icon">🔍</div>
      <h2 className="pub-reg-state-title">Registration Not Found</h2>
      <p className="pub-reg-state-msg">
        This registration form doesn't exist or may have been removed.
        Contact the league organizer for more information.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Success state
// ---------------------------------------------------------------------------

function SuccessState({ league, form, registrantName, registrantEmail, role, teamName, teamId, teams, paymentPaid }) {
  const joinedTeam = teams.find(t => t.id === teamId);
  const paymentRequired = form?.requirePayment;
  const feeAmount = form?.feeAmount;

  return (
    <div className="pub-reg-state-card pub-reg-success-card">
      <div className="pub-reg-success-icon">✓</div>
      <h2 className="pub-reg-state-title">
        {paymentRequired && paymentPaid ? 'Payment Confirmed!' : "You're Registered!"}
      </h2>
      <p className="pub-reg-state-msg">
        Thanks, <strong>{registrantName}</strong>! A confirmation has been sent to <strong>{registrantEmail}</strong>.
      </p>

      {form?.registrationMode === 'team' && role === 'captain' && teamName && (
        <div className="pub-reg-success-detail">
          Team registered: <strong>{teamName}</strong>
        </div>
      )}
      {form?.registrationMode === 'team' && role === 'player' && joinedTeam && (
        <div className="pub-reg-success-detail">
          Joined team: <strong>{joinedTeam.name}</strong>
        </div>
      )}

      {paymentRequired && paymentPaid && (
        <div className="pub-reg-payment-notice pub-reg-payment-paid">
          <strong>Payment received</strong> — ${parseFloat(feeAmount).toFixed(2)} paid successfully.
        </div>
      )}
      {paymentRequired && !paymentPaid && (
        <div className="pub-reg-payment-notice">
          <strong>Payment pending</strong> — Your registration fee of ${parseFloat(feeAmount).toFixed(2)} will be
          collected by the league organizer.
        </div>
      )}

      {league?.slug && (
        <a href={`/public/${league.slug}`} className="pub-reg-state-link" style={{ marginTop: '20px', display: 'inline-block' }}>
          View league page →
        </a>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Payment cancelled state — shown when user returns from Stripe with ?payment=cancelled
// ---------------------------------------------------------------------------

function PaymentCancelledState({ league, form, registrationId, onRetryPayment, retrying }) {
  const feeAmount = form?.feeAmount;
  return (
    <div className="pub-reg-state-card">
      <div className="pub-reg-state-icon">⚠️</div>
      <h2 className="pub-reg-state-title">Payment Cancelled</h2>
      <p className="pub-reg-state-msg">
        Your registration has been saved, but payment was not completed.
        {feeAmount && ` A fee of $${parseFloat(feeAmount).toFixed(2)} is required to secure your spot.`}
      </p>
      <button
        className="pub-reg-submit-btn"
        style={{ maxWidth: '260px', margin: '16px auto 0' }}
        onClick={onRetryPayment}
        disabled={retrying}
      >
        {retrying ? 'Redirecting to payment...' : 'Complete Payment →'}
      </button>
      {league?.slug && (
        <a href={`/public/${league.slug}`} className="pub-reg-state-link" style={{ marginTop: '16px', display: 'block' }}>
          View league page →
        </a>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function PublicRegistrationPage() {
  const { leagueId } = useParams();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [pageState, setPageState] = useState('open'); // 'open' | 'closed' | 'draft' | 'not_found' | 'error'
  const [league, setLeague] = useState(null);
  const [form, setForm] = useState(null);
  const [teams, setTeams] = useState([]);
  const [stripeReady, setStripeReady] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [paymentPaid, setPaymentPaid] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Payment cancelled flow — captured from URL params
  const [paymentCancelled, setPaymentCancelled] = useState(false);
  const [cancelledRegistrationId, setCancelledRegistrationId] = useState(null);
  const [retryingPayment, setRetryingPayment] = useState(false);

  // Returning player recognition
  const [returningPlayer, setReturningPlayer] = useState(null); // { name, phone } if found

  // Form fields
  const [registrantName, setRegistrantName] = useState('');
  const [registrantEmail, setRegistrantEmail] = useState('');
  const [registrantPhone, setRegistrantPhone] = useState('');
  const [role, setRole] = useState('player'); // 'captain' | 'player'
  const [teamName, setTeamName] = useState('');
  const [teamId, setTeamId] = useState('');
  const [customFields, setCustomFields] = useState({});

  useEffect(() => {
    // Check URL params first — handle Stripe return URLs
    const paymentParam = searchParams.get('payment');
    if (paymentParam === 'success') {
      setPaymentPaid(true);
      setSubmitted(true);
    } else if (paymentParam === 'cancelled') {
      const regId = searchParams.get('registration_id');
      setPaymentCancelled(true);
      setCancelledRegistrationId(regId);
    }

    async function load() {
      try {
        const data = await getPublicRegistrationForm(leagueId);
        setLeague(data.league);
        setForm(data.form);
        setTeams(data.teams || []);
        setStripeReady(!!data.stripeReady);
        setPageState('open');
      } catch (err) {
        const status = err.response?.status;
        if (status === 404) {
          setPageState('not_found');
        } else if (status === 403) {
          const formStatus = err.response?.data?.status || 'closed';
          setPageState(formStatus);
        } else {
          const msg = err.message || '';
          if (msg.includes('not found') || msg.includes('No registration form')) {
            setPageState('not_found');
          } else {
            setPageState('error');
          }
        }
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [leagueId, searchParams]);

  const handleCustomFieldChange = (fieldId, value) => {
    setCustomFields(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleEmailBlur = async (email) => {
    const trimmed = email.trim();
    if (!trimmed || !trimmed.includes('@')) return;
    const result = await checkRegistrantEmail(leagueId, trimmed);
    if (result.found) {
      setReturningPlayer(result);
      if (!registrantName) setRegistrantName(result.name || '');
      if (!registrantPhone && result.phone) setRegistrantPhone(result.phone);
    } else {
      setReturningPlayer(null);
    }
  };

  /** Redirect to Stripe Checkout for an existing pending registration. */
  const redirectToCheckout = async (registrationId) => {
    try {
      const { url } = await createCheckoutSession(leagueId, registrationId);
      window.location.href = url;
    } catch (err) {
      setSubmitError(err.message || 'Failed to start payment. Please try again.');
      setSubmitting(false);
      setRetryingPayment(false);
    }
  };

  const handleRetryPayment = async () => {
    setRetryingPayment(true);
    await redirectToCheckout(cancelledRegistrationId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitting(true);

    try {
      const payload = {
        registrantName: registrantName.trim(),
        registrantEmail: registrantEmail.trim(),
        registrantPhone: registrantPhone.trim() || undefined,
        customFields: Object.keys(customFields).length > 0 ? customFields : undefined,
      };

      if (form.registrationMode === 'team') {
        payload.role = role;
        if (role === 'captain') {
          payload.teamName = teamName.trim();
        } else {
          payload.teamId = teamId;
        }
      }

      const result = await submitRegistration(leagueId, payload);

      // If payment is required and Stripe is set up, redirect to Stripe Checkout
      if (result.requiresPayment && result.stripeReady && result.registration?.id) {
        await redirectToCheckout(result.registration.id);
        return; // browser will navigate away; don't update state
      }

      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setSubmitError(err.message || 'Failed to submit registration. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // --- Derived ---
  const isTeamMode = form?.registrationMode === 'team';
  const isCaptain = role === 'captain';
  const teamsAtCap = form?.isTeamsFull;
  const availableTeams = teams.filter(t => !t.isFull);
  const sportLabel = league?.sport && league.sport !== 'general'
    ? league.sport.charAt(0).toUpperCase() + league.sport.slice(1)
    : null;

  // --- Render header (shown on all states) ---
  const renderHeader = () => (
    <>
      <div className="pub-reg-branding-bar">
        <a href="https://leaguemaker.app" target="_blank" rel="noopener noreferrer" className="pub-reg-brand-logo">
          <Logo variant="white" size="1.1rem" />
        </a>
        <a href="https://leaguemaker.app/signup" target="_blank" rel="noopener noreferrer" className="pub-reg-brand-cta">
          Start a League — Free
        </a>
      </div>

      {league && (
        <div className="pub-reg-league-header">
          <h1 className="pub-reg-league-name">{league.name}</h1>
          <div className="pub-reg-league-meta">
            {sportLabel && <span className="pub-reg-sport-badge">{sportLabel}</span>}
            {league.seasonYear && <span className="pub-reg-season">Season {league.seasonYear}</span>}
          </div>
          <p className="pub-reg-header-sub">League Registration</p>
        </div>
      )}
    </>
  );

  // --- Loading ---
  if (loading) {
    return (
      <div className="pub-reg-page">
        {renderHeader()}
        <div className="pub-reg-content">
          <div className="pub-reg-loading">Loading registration form...</div>
        </div>
      </div>
    );
  }

  // --- Error / not found / closed states ---
  if (pageState === 'not_found' || pageState === 'error') {
    return (
      <div className="pub-reg-page">
        {renderHeader()}
        <div className="pub-reg-content">
          <NotFoundState />
        </div>
      </div>
    );
  }

  if (pageState === 'closed' || pageState === 'draft') {
    return (
      <div className="pub-reg-page">
        {renderHeader()}
        <div className="pub-reg-content">
          <ClosedState league={league} closedStatus={pageState} />
        </div>
      </div>
    );
  }

  // --- Payment cancelled state ---
  if (paymentCancelled && !submitted) {
    return (
      <div className="pub-reg-page">
        {renderHeader()}
        <div className="pub-reg-content">
          <PaymentCancelledState
            league={league}
            form={form}
            registrationId={cancelledRegistrationId}
            onRetryPayment={handleRetryPayment}
            retrying={retryingPayment}
          />
        </div>
      </div>
    );
  }

  // --- Success state ---
  if (submitted) {
    return (
      <div className="pub-reg-page">
        {renderHeader()}
        <div className="pub-reg-content">
          <SuccessState
            league={league}
            form={form}
            registrantName={registrantName}
            registrantEmail={registrantEmail}
            role={role}
            teamName={teamName}
            teamId={teamId}
            teams={teams}
            paymentPaid={paymentPaid}
          />
        </div>
      </div>
    );
  }

  // --- Open: show form ---
  const fields = form?.fields || [];

  return (
    <div className="pub-reg-page">
      {renderHeader()}

      <div className="pub-reg-content">
        <form className="pub-reg-form" onSubmit={handleSubmit} noValidate>

          {/* Dates / capacity info */}
          {(form?.closesAt || form?.maxTeams) && (
            <div className="pub-reg-info-bar">
              {form.closesAt && (
                <span>
                  Closes: <strong>{new Date(form.closesAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</strong>
                </span>
              )}
              {form.maxTeams && isTeamMode && (
                <span>
                  Teams: <strong>{form.registeredTeamCount ?? 0} / {form.maxTeams}</strong>
                </span>
              )}
            </div>
          )}

          {/* Payment notice */}
          {form?.requirePayment && (
            <div className="pub-reg-payment-notice pub-reg-payment-notice-top">
              <strong>Registration fee:</strong>{' '}
              {form.feeType === 'flat_team' && `$${parseFloat(form.feeAmount).toFixed(2)} per team`}
              {form.feeType === 'per_player' && `$${parseFloat(form.feeAmount).toFixed(2)} per player`}
              {stripeReady
                ? ' — You will be redirected to a secure payment page after registering.'
                : ' — Payment will be collected by the league organizer.'}
            </div>
          )}

          {/* Team mode: teams at capacity warning */}
          {isTeamMode && teamsAtCap && isCaptain && (
            <div className="pub-reg-warning">
              The maximum number of teams has been reached. You can still join an existing team as a player.
            </div>
          )}

          {/* ── Section: Your Info ── */}
          <div className="pub-reg-section">
            <h3 className="pub-reg-section-title">Your Information</h3>

            <div className="pub-reg-field-row">
              <label className="pub-reg-label">
                Full Name <span className="pub-reg-required">*</span>
              </label>
              <input
                type="text"
                className="pub-reg-input"
                value={registrantName}
                onChange={e => setRegistrantName(e.target.value)}
                placeholder="Jane Smith"
                required
                autoComplete="name"
              />
            </div>

            <div className="pub-reg-field-row">
              <label className="pub-reg-label">
                Email Address <span className="pub-reg-required">*</span>
              </label>
              <input
                type="email"
                className="pub-reg-input"
                value={registrantEmail}
                onChange={e => { setRegistrantEmail(e.target.value); setReturningPlayer(null); }}
                onBlur={e => handleEmailBlur(e.target.value)}
                placeholder="jane@example.com"
                required
                autoComplete="email"
              />
              {returningPlayer && (
                <div className="pub-reg-returning-banner">
                  Welcome back, <strong>{returningPlayer.name}</strong>! Your info has been pre-filled.
                </div>
              )}
            </div>

            <div className="pub-reg-field-row">
              <label className="pub-reg-label">Phone Number</label>
              <input
                type="tel"
                className="pub-reg-input"
                value={registrantPhone}
                onChange={e => setRegistrantPhone(e.target.value)}
                placeholder="(555) 000-0000"
                autoComplete="tel"
              />
            </div>
          </div>

          {/* ── Section: Team (team mode only) ── */}
          {isTeamMode && (
            <div className="pub-reg-section">
              <h3 className="pub-reg-section-title">Team</h3>

              <div className="pub-reg-role-cards">
                <RoleCard
                  selected={role === 'captain'}
                  onClick={() => setRole('captain')}
                  icon="🏆"
                  title="Register a Team"
                  description="Create a new team and sign up as the captain"
                />
                <RoleCard
                  selected={role === 'player'}
                  onClick={() => setRole('player')}
                  icon="👤"
                  title="Join a Team"
                  description="Join an existing team as a player"
                />
              </div>

              {role === 'captain' && (
                <div className="pub-reg-field-row" style={{ marginTop: '16px' }}>
                  <label className="pub-reg-label">
                    Team Name <span className="pub-reg-required">*</span>
                  </label>
                  <input
                    type="text"
                    className="pub-reg-input"
                    value={teamName}
                    onChange={e => setTeamName(e.target.value)}
                    placeholder="e.g. The Blue Eagles"
                    required
                  />
                </div>
              )}

              {role === 'player' && (
                <div className="pub-reg-field-row" style={{ marginTop: '16px' }}>
                  <label className="pub-reg-label">
                    Select Team <span className="pub-reg-required">*</span>
                  </label>
                  {teams.length === 0 ? (
                    <p className="pub-reg-field-note">
                      No teams have registered yet. Be the first by registering a team above!
                    </p>
                  ) : availableTeams.length === 0 ? (
                    <p className="pub-reg-field-note">All teams are currently full.</p>
                  ) : (
                    <select
                      className="pub-reg-input"
                      value={teamId}
                      onChange={e => setTeamId(e.target.value)}
                      required
                    >
                      <option value="">Choose a team...</option>
                      {teams.map(team => (
                        <option key={team.id} value={team.id} disabled={team.isFull}>
                          {team.name}
                          {team.maxPlayers ? ` (${team.playerCount}/${team.maxPlayers})` : ` (${team.playerCount} registered)`}
                          {team.isFull ? ' — Full' : ''}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Section: Custom Fields ── */}
          {fields.length > 0 && (
            <div className="pub-reg-section">
              <h3 className="pub-reg-section-title">Additional Information</h3>
              {fields.map(field => {
                // Waiver fields render their own label (the label IS the waiver text)
                const isWaiver = field.fieldType === 'waiver';
                return (
                  <div key={field.id} className="pub-reg-field-row">
                    {!isWaiver && (
                      <label className="pub-reg-label">
                        {field.label}
                        {field.required && <span className="pub-reg-required"> *</span>}
                      </label>
                    )}
                    <CustomField
                      field={field}
                      value={customFields[field.id]}
                      onChange={handleCustomFieldChange}
                    />
                  </div>
                );
              })}
            </div>
          )}

          {/* Submit error */}
          {submitError && (
            <div className="pub-reg-error">{submitError}</div>
          )}

          {/* Submit button */}
          <button
            type="submit"
            className="pub-reg-submit-btn"
            disabled={submitting}
          >
            {submitting
              ? (form?.requirePayment && stripeReady ? 'Redirecting to payment...' : 'Submitting...')
              : (form?.requirePayment && stripeReady
                  ? `Register & Pay $${parseFloat(form.feeAmount).toFixed(2)} →`
                  : 'Complete Registration')}
          </button>

          <p className="pub-reg-footer-note">
            By registering, you confirm that the information provided is accurate.
          </p>
        </form>
      </div>
    </div>
  );
}
